/** sessionStorage: applied studies-list filters for GET /grader/study/index */
export const GRADER_STUDY_INDEX_FILTERS_SESSION_KEY = 'graderStudyIndexFilters';

export function getDefaultStudiesListFilters({ isAdmin = false, userId = null } = {}) {
  const userIdString = userId != null ? String(userId) : null;
  return {
    grader_ids: isAdmin ? 'all' : userIdString || 'all',
    client_ids: 'all',
    protocol_ids: 'all',
    status: '',
    indicator_escalated: false,
    indicator_flagged: false,
    indicator_random_sampled: false,
    indicator_missing_fields: false,
    outliers: 'none', // default: No Filter (no outlier API params)
    start_date: '',
    end_date: '',
    search: '',
  };
}

/** Map applied API outlier flags → StudiesList dropdown value. Default: none (No Filter). */
function resolveOutliersFilterValue(apiFilters = {}) {
  if (apiFilters.is_too_fast) {
    return 'too_fast';
  }
  if (apiFilters.is_excessive) {
    return 'excessive';
  }
  if (apiFilters.has_outliers) {
    return 'all';
  }
  return 'none';
}

function mapApiStatusToUi(statusValue) {
  if (!statusValue) {
    return '';
  }
  const normalized = String(statusValue).toUpperCase().replace(/[-\s]/g, '_');
  if (normalized === 'IN_PROGRESS' || normalized === 'INPROGRESS') {
    return 'IN_PROGRESS';
  }
  if (normalized === 'ASSIGNED') {
    return 'ASSIGNED';
  }
  if (normalized === 'COMPLETED' || normalized === 'COMPLETE') {
    return 'COMPLETED';
  }
  if (normalized === 'PENDING') {
    return 'PENDING';
  }
  return String(statusValue);
}

/**
 * Map GET /grader/study/index `filters` (API shape) to StudiesList filter state.
 */
export function mapApiFiltersToStudiesListFilters(apiFilters, baseFilters) {
  if (!apiFilters || typeof apiFilters !== 'object') {
    return null;
  }
  const next = { ...(baseFilters || getDefaultStudiesListFilters()) };

  if (apiFilters.grader_id != null && apiFilters.grader_id !== '') {
    next.grader_ids = String(apiFilters.grader_id);
  }
  if (apiFilters.client_id != null && apiFilters.client_id !== '') {
    next.client_ids = String(apiFilters.client_id);
  }
  if (apiFilters.protocol_id != null && apiFilters.protocol_id !== '') {
    next.protocol_ids = String(apiFilters.protocol_id);
  }
  if (apiFilters.status != null && apiFilters.status !== '') {
    next.status = mapApiStatusToUi(apiFilters.status);
  }
  if (apiFilters.start_date) {
    next.start_date = apiFilters.start_date;
  }
  if (apiFilters.end_date) {
    next.end_date = apiFilters.end_date;
  }
  if (apiFilters.search) {
    next.search = apiFilters.search;
  }
  if (apiFilters.is_escalated != null) {
    next.indicator_escalated = Boolean(apiFilters.is_escalated);
  }
  if (apiFilters.is_flagged != null) {
    next.indicator_flagged = Boolean(apiFilters.is_flagged);
  }
  if (apiFilters.is_random_sampled != null) {
    next.indicator_random_sampled = Boolean(apiFilters.is_random_sampled);
  }
  if (apiFilters.has_missing_fields != null) {
    next.indicator_missing_fields = Boolean(apiFilters.has_missing_fields);
  }

  next.outliers = resolveOutliersFilterValue(apiFilters);

  return next;
}

function normalizeApiFiltersFromResponse(apiFilters) {
  if (!apiFilters || typeof apiFilters !== 'object') {
    return {};
  }
  const params = {};
  if (apiFilters.grader_id != null && apiFilters.grader_id !== '' && apiFilters.grader_id !== 'all') {
    params.grader_id = String(apiFilters.grader_id);
  }
  if (apiFilters.client_id != null && apiFilters.client_id !== '' && apiFilters.client_id !== 'all') {
    params.client_id = String(apiFilters.client_id);
  }
  if (apiFilters.protocol_id != null && apiFilters.protocol_id !== '' && apiFilters.protocol_id !== 'all') {
    params.protocol_id = String(apiFilters.protocol_id);
  }
  const status = mapStatusFilterToApi(apiFilters.status);
  if (status) {
    params.status = status;
  }
  if (apiFilters.start_date) {
    params.start_date = apiFilters.start_date;
  }
  if (apiFilters.end_date) {
    params.end_date = apiFilters.end_date;
  }
  if (apiFilters.search) {
    params.search = apiFilters.search;
  }
  if (apiFilters.is_escalated != null) {
    params.is_escalated = Boolean(apiFilters.is_escalated);
  }
  if (apiFilters.is_flagged != null) {
    params.is_flagged = Boolean(apiFilters.is_flagged);
  }
  if (apiFilters.is_random_sampled != null) {
    params.is_random_sampled = Boolean(apiFilters.is_random_sampled);
  }
  if (apiFilters.has_missing_fields != null) {
    params.has_missing_fields = Boolean(apiFilters.has_missing_fields);
  }
  if (apiFilters.has_outliers != null) {
    params.has_outliers = Boolean(apiFilters.has_outliers);
  }
  if (apiFilters.is_too_fast != null) {
    params.is_too_fast = Boolean(apiFilters.is_too_fast);
  }
  if (apiFilters.is_excessive != null) {
    params.is_excessive = Boolean(apiFilters.is_excessive);
  }
  return params;
}

/**
 * Restore StudiesList filter state after viewer → studies list (sessionStorage).
 */
export function readStudiesListFiltersFromSession(context = {}) {
  const defaults = getDefaultStudiesListFilters(context);
  if (typeof sessionStorage === 'undefined') {
    return defaults;
  }
  try {
    const raw = sessionStorage.getItem(GRADER_STUDY_INDEX_FILTERS_SESSION_KEY);
    if (!raw) {
      return defaults;
    }
    const parsed = JSON.parse(raw);
    if (parsed?.uiFilters && typeof parsed.uiFilters === 'object') {
      const apiParams =
        parsed?.apiParams && typeof parsed.apiParams === 'object' ? parsed.apiParams : {};
      return {
        ...defaults,
        ...parsed.uiFilters,
        // Outliers default to No Filter unless an outlier param was actually applied
        outliers: resolveOutliersFilterValue(apiParams),
      };
    }
    if (parsed?.apiParams && typeof parsed.apiParams === 'object') {
      const mapped = mapApiFiltersToStudiesListFilters(parsed.apiParams, defaults);
      return mapped || defaults;
    }
  } catch (e) {
    console.warn('[graderStudyIndexFilters] Failed to read filters from session:', e);
  }
  return defaults;
}

export function mapStatusFilterToApi(statusValue) {
  if (!statusValue) {
    return undefined;
  }
  const normalized = statusValue.toLowerCase().replace(/[-\s]/g, '_');
  if (normalized === 'in_progress' || normalized === 'inprogress') {
    return 'IN_PROGRESS';
  }
  if (normalized === 'assigned') {
    return 'ASSIGNED';
  }
  return statusValue;
}

export function getStudyIndicatorFlagsFromCheckboxes(filters) {
  const params = {};

  // Existing indicator toggles
  if (filters.indicator_escalated) {
    params.is_escalated = true;
  }
  if (filters.indicator_flagged) {
    params.is_flagged = true;
  }
  if (filters.indicator_random_sampled) {
    params.is_random_sampled = true;
  }

  // New admin/manager-only indicator toggle:
  // only send when ON; omit when OFF
  if (filters.indicator_missing_fields) {
    params.has_missing_fields = true;
  }

  // New admin/manager-only outliers dropdown
  const outliersValue = (filters.outliers || 'none').toString().trim().toLowerCase();
  if (outliersValue === 'all') {
    params.has_outliers = true;
  } else if (outliersValue === 'too_fast') {
    params.is_too_fast = true;
  } else if (outliersValue === 'excessive') {
    params.is_excessive = true;
  }
  // 'none' => omit outlier params

  return params;
}

/**
 * Query params for /grader/study/index — only filters the user has applied (non-default).
 */
export function buildAppliedStudiesListFiltersForApi(filters, { isAdmin = false, userId = null } = {}) {
  const params = {};

  let graderId = 'all';
  if (isAdmin) {
    graderId = filters.grader_ids || 'all';
  } else {
    const userIdString = userId != null ? String(userId) : null;
    if (filters.grader_ids && filters.grader_ids !== 'all') {
      graderId = filters.grader_ids;
    } else if (userIdString) {
      graderId = userIdString;
    }
  }
  if (graderId && graderId !== 'all') {
    params.grader_id = graderId;
  }

  const clientId = filters.client_ids || 'all';
  if (clientId && clientId !== 'all') {
    params.client_id = clientId;
  }

  const protocolId = filters.protocol_ids || 'all';
  if (protocolId && protocolId !== 'all') {
    params.protocol_id = protocolId;
  }

  const status = mapStatusFilterToApi(filters.status);
  if (status) {
    params.status = status;
  }

  if (filters.start_date) {
    params.start_date = filters.start_date;
  }
  if (filters.end_date) {
    params.end_date = filters.end_date;
  }
  if (filters.search) {
    params.search = filters.search;
  }

  Object.assign(params, getStudyIndicatorFlagsFromCheckboxes(filters));

  return params;
}

export function persistGraderStudyIndexFilters(filters, context = {}) {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  try {
    const apiParams = buildAppliedStudiesListFiltersForApi(filters, context);
    if (Object.keys(apiParams).length > 0) {
      sessionStorage.setItem(
        GRADER_STUDY_INDEX_FILTERS_SESSION_KEY,
        JSON.stringify({ apiParams, uiFilters: filters })
      );
    } else {
      sessionStorage.removeItem(GRADER_STUDY_INDEX_FILTERS_SESSION_KEY);
    }
  } catch (e) {
    console.warn('[graderStudyIndexFilters] Failed to persist filters:', e);
  }
}

/**
 * Persist filters from GET /grader/study/index `data[0].filters` (authoritative server state).
 */
export function persistGraderStudyIndexFiltersFromApiResponse(apiFilters, context = {}) {
  if (typeof sessionStorage === 'undefined' || !apiFilters || typeof apiFilters !== 'object') {
    return;
  }
  try {
    const defaults = getDefaultStudiesListFilters(context);
    const uiFilters = mapApiFiltersToStudiesListFilters(apiFilters, defaults);
    const apiParams = normalizeApiFiltersFromResponse(apiFilters);
    if (Object.keys(apiParams).length === 0) {
      sessionStorage.removeItem(GRADER_STUDY_INDEX_FILTERS_SESSION_KEY);
      return;
    }
    sessionStorage.setItem(
      GRADER_STUDY_INDEX_FILTERS_SESSION_KEY,
      JSON.stringify({ apiParams, uiFilters: uiFilters || defaults })
    );
  } catch (e) {
    console.warn('[graderStudyIndexFilters] Failed to persist API response filters:', e);
  }
}

export function readAppliedGraderStudyIndexFiltersFromSession() {
  if (typeof sessionStorage === 'undefined') {
    return {};
  }
  try {
    const raw = sessionStorage.getItem(GRADER_STUDY_INDEX_FILTERS_SESSION_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    if (parsed?.apiParams && typeof parsed.apiParams === 'object') {
      return parsed.apiParams;
    }
    return {};
  } catch {
    return {};
  }
}

export function clearGraderStudyIndexFiltersSession() {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  try {
    sessionStorage.removeItem(GRADER_STUDY_INDEX_FILTERS_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Resolve grading study id for GET /grader/study/index (explicit arg or session key `id`).
 */
export function resolveGraderStudyIndexStudyId(studyId) {
  if (studyId != null && studyId !== '') {
    return String(studyId);
  }
  if (typeof sessionStorage !== 'undefined') {
    const fromSession = sessionStorage.getItem('id');
    if (fromSession) {
      return fromSession;
    }
  }
  return null;
}

/**
 * Full query string params for GET /grader/study/index.
 */
export function buildGraderStudyIndexQueryParams(studyId, options = {}) {
  const resolvedStudyId = resolveGraderStudyIndexStudyId(studyId);
  const queryParams = {};
  if (resolvedStudyId) {
    queryParams.study_id = resolvedStudyId;
  }

  const submissionId =
    options.submission_id ??
    (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('currentSubmissionId') : null);
  if (submissionId) {
    queryParams.submission_id = submissionId;
  }

  const appliedFilters =
    options.filters && typeof options.filters === 'object'
      ? options.filters
      : readAppliedGraderStudyIndexFiltersFromSession();

  Object.assign(queryParams, appliedFilters);

  return queryParams;
}
